import React, { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';

import { cn } from './cn';

export interface CategoryChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  icon?: React.ReactNode;
  active?: boolean;
  density?: 'comfortable' | 'compact';
}

export const CategoryChip = forwardRef<HTMLButtonElement, CategoryChipProps>(
  (
    { label, icon, active = false, density = 'comfortable', className, onKeyDown, onClick, ...props },
    ref,
  ) => {
    const sizeClasses =
      density === 'compact'
        ? 'h-10 px-3 text-sm md:h-8 md:px-3 md:text-xs'
        : 'h-11 px-4 text-sm';
    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        aria-selected={active}
        aria-pressed={active}
        aria-current={active ? 'true' : undefined}
        tabIndex={active ? 0 : -1}
        title={label}
        onClick={onClick}
        onKeyDown={onKeyDown}
        className={cn(
          'group inline-flex shrink-0 items-center justify-start gap-2 rounded-full border font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          sizeClasses,
          active
            ? 'border-transparent bg-primary text-primary-fg shadow-md shadow-primary/20 scale-[1.02] font-semibold'
            : 'border-border bg-surface text-text hover:bg-muted/70 hover:border-slate-300 hover:shadow-sm',
          'max-w-full truncate transition-all duration-200 active:scale-[0.98]',
          className,
        )}
        {...props}
      >
        {icon ? (
          <span
            aria-hidden
            className={cn(
              'flex shrink-0 items-center text-base text-text',
              density === 'compact' && 'text-sm md:text-base',
            )}
          >
            {icon}
          </span>
        ) : null}
        <span
          className={cn(
            'truncate text-sm',
            density === 'compact' && 'text-sm md:text-xs md:leading-[1.35]',
          )}
          aria-hidden={false}
        >
          {label}
        </span>
      </button>
    );
  },
);

CategoryChip.displayName = 'CategoryChip';

export default CategoryChip;
